import React, { Fragment } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  Icon as MuiIcon,
  Input,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from "@material-ui/core";
import { useForm } from "react-hook-form";
import styled from "styled-components";
import _ from "lodash";
import Chip from "@material-ui/core/Chip";
import { useTranslation } from "react-i18next";

const Icon = styled(MuiIcon)`
  margin-right: 8px;
`;

/** Normaliza Motivo many2many: array, valor único ou vazio → array de ids. */
function toReasonIds(selected) {
  if (Array.isArray(selected)) return selected;
  if (selected) return [selected];
  return [];
}

const MultiOccurrencesForm = ({ types, onChange, defaultAbsence = false }) => {
  const { t } = useTranslation();
  const { register, setValue, getValues, watch } = useForm({
    defaultValues: { occurrences: [], absence: defaultAbsence }
  });

  const handleChange = (field, val) => {
    if (field) setValue(field, val);

    onChange(getValues());
  };

  /**
   * Select onChange envia evento; remoção de Chip envia o novo array diretamente.
   */
  const applyOccurrencesValue = val => {
    const next = Array.isArray(val) ? val : [];
    const oldVal = getValues("occurrences") || [];

    const removed = _.difference(oldVal, next);
    if (removed.length && removed[0].reasons && removed[0].reasons.length) {
      setValue(`reason-${removed[0].id}`, []);
      setValue(`description-${removed[0].id}`, null);
    }
    handleChange("occurrences", next);
  };

  const onChangeOccurrences = e => {
    const val = e && e.target && "value" in e.target ? e.target.value : e;
    applyOccurrencesValue(val);
  };

  return (
    <Grid
      container
      spacing={2}
      justifyContent="space-around"
      alignItems="center"
    >
      <Grid item xs={5}>
        <FormControl fullWidth>
          <InputLabel>{t("occurrences.launch")}</InputLabel>
          <Select
            multiple
            {...register("occurrences")}
            value={watch("occurrences") || []}
            onChange={onChangeOccurrences}
            disabled={watch("absence")}
            input={<Input />}
            renderValue={selected => (
              <div>
                {selected.map(selectedValue => (
                  <Chip
                    onDelete={() => {
                      const current = getValues("occurrences") || [];
                      const next = current.filter(
                        o => o.id !== selectedValue.id
                      );
                      applyOccurrencesValue(next);
                    }}
                    key={selectedValue.id}
                    label={selectedValue.name}
                    icon={selectedValue.icon}
                    onMouseDown={e => e.stopPropagation()}
                  />
                ))}
              </div>
            )}
          >
            {types.map(occur => (
              <MenuItem key={occur.id} value={occur}>
                <Icon>{occur.icon}</Icon>
                {occur.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={2} />
      <Grid item xs={2}>
        <FormControlLabel
          control={(
            <Checkbox
              checked={!!watch("absence")}
              onChange={e => {
                setValue("occurrences", []);
                handleChange("absence", e.target.checked);
              }}
              color="primary"
            />
          )}
          label={t("occurrences.absent")}
        />
      </Grid>
      {(watch("occurrences") || [])
        .filter(occur => occur.reasons && occur.reasons.length)
        .map(occur => {
          const reasonIds = toReasonIds(watch(`reason-${occur.id}`));

          return (
            <Fragment key={occur.id}>
              <Grid item xs={12}>
                <Box mt={3}>
                  <Typography variant="body1" color="textSecondary">
                    {t("occurrences.detailsAbout", { name: occur.name })}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={5}>
                <FormControl fullWidth>
                  <InputLabel
                    shrink={reasonIds.length > 0}
                    id={`multi-motivo-label-${occur.id}`}
                  >
                    {t("occurrences.reason")}
                  </InputLabel>
                  <Select
                    multiple
                    labelId={`multi-motivo-label-${occur.id}`}
                    value={reasonIds}
                    onChange={e =>
                      handleChange(`reason-${occur.id}`, e.target.value)}
                    input={<Input />}
                    renderValue={selected => (
                      <Box display="flex" flexWrap="wrap" style={{ gap: 4 }}>
                        {(selected || []).map(id => {
                          const reason = occur.reasons.find(
                            item => item.id === id
                          );
                          return (
                            <Chip
                              key={id}
                              size="small"
                              label={(reason && reason.name) || id}
                              onDelete={() => {
                                handleChange(
                                  `reason-${occur.id}`,
                                  reasonIds.filter(rid => rid !== id)
                                );
                              }}
                              onMouseDown={e => e.stopPropagation()}
                            />
                          );
                        })}
                      </Box>
                    )}
                  >
                    {occur.reasons.map(reason => (
                      <MenuItem key={reason.id} value={reason.id}>
                        <Checkbox
                          color="primary"
                          checked={reasonIds.includes(reason.id)}
                        />
                        {reason.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={1} />
              <Grid item xs={5}>
                {reasonIds.length > 0 && (
                  <TextField
                    placeholder={t("common.labels.notesOptional")}
                    value={watch(`description-${occur.id}`) ?? ""}
                    onChange={e =>
                      handleChange(`description-${occur.id}`, e.target.value)}
                    fullWidth
                    margin="normal"
                  />
                )}
              </Grid>
            </Fragment>
          );
        })}
    </Grid>
  );
};

MultiOccurrencesForm.propTypes = {
  onChange: PropTypes.func.isRequired,
  defaultAbsence: PropTypes.bool,
  types: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      icon: PropTypes.string,
      name: PropTypes.string
    })
  ).isRequired
};

MultiOccurrencesForm.defaultProps = {
  defaultAbsence: false
};

export default MultiOccurrencesForm;
